import {
  API_PREFIX,
  paginatedFromDto,
  paginationMetaDtoSchema,
  type HttpClient,
} from "@nasam/kernel";
import { z } from "zod";

import { BULK_STATUSES } from "../../../shared/bulkUpload";
import {
  POI_ICONS,
  type CreatePoiInput,
  type Poi,
  type PoiBulkRow,
  type PoiExportQuery,
  type PoiListQuery,
  type PoiMapQuery,
  type PoisRepository,
  type UpdatePoiInput,
} from "../domain";

const POIS_PATH = `${API_PREFIX}/pois`;
const POIS_MAP_PATH = `${POIS_PATH}/map`;
const POIS_EXPORT_PATH = `${POIS_PATH}/export`;
const POIS_BULK_PATH = `${POIS_PATH}/bulk-upload`;
const POIS_TEMPLATE_PATH = `${POIS_BULK_PATH}/template`;
const POIS_BULK_PREVIEW_PATH = `${POIS_BULK_PATH}/preview`;
const POIS_BULK_ERROR_REPORT_PATH = `${POIS_BULK_PATH}/error-report`;

const poiDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  icon: z.enum(POI_ICONS),
  remarks: z.string().nullish(),
  accountId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const createResponseSchema = z.object({
  message: z.string().optional(),
  data: poiDtoSchema,
});

// Rows also name their `account`, which the screen doesn't show (a client
// user only sees their own company's POIs).
const listResponseSchema = z.object({
  data: z.array(poiDtoSchema),
  meta: paginationMetaDtoSchema,
});

const mapResponseSchema = z.object({
  data: z.array(poiDtoSchema),
});

const detailResponseSchema = z.object({
  data: poiDtoSchema,
});

const deleteResponseSchema = z.object({
  message: z.string().optional(),
});

// A row's values are as read from the sheet: an invalid row may carry a
// coordinate that isn't a number, or an icon outside POI_ICONS.
const bulkCellSchema = z.union([z.number(), z.string()]).nullish();

const bulkRowDtoSchema = z.object({
  row: z.number(),
  name: z.string().nullish(),
  latitude: bulkCellSchema,
  longitude: bulkCellSchema,
  icon: z.string().nullish(),
  remarks: z.string().nullish(),
  status: z.enum(BULK_STATUSES),
  errors: z.array(z.string()).default([]),
});

const bulkPreviewResponseSchema = z.object({
  data: z.object({
    totalRecords: z.number(),
    validRecords: z.number(),
    invalidRecords: z.number(),
    duplicateRecords: z.number(),
    rows: z.array(bulkRowDtoSchema),
  }),
});

const bulkImportResponseSchema = z.object({
  message: z.string().optional(),
  data: z.object({
    totalRecords: z.number(),
    createdRecords: z.number(),
    failedRecords: z.number(),
    failedRows: z.array(bulkRowDtoSchema).default([]),
  }),
});

function bulkRowFromDto(dto: z.infer<typeof bulkRowDtoSchema>): PoiBulkRow {
  return {
    ...dto,
    name: dto.name ?? null,
    latitude: dto.latitude ?? null,
    longitude: dto.longitude ?? null,
    icon: dto.icon ?? null,
    remarks: dto.remarks ?? null,
  };
}

/** Bulk Upload's body: the sheet in the `file` field. */
function bulkBody(file: File): FormData {
  const body = new FormData();
  body.set("file", file, file.name);
  return body;
}

/** `search` only when there is one: an empty one is no search at all. */
function searchParam(search: string | undefined) {
  const trimmed = search?.trim();
  return trimmed === undefined || trimmed === "" ? {} : { search: trimmed };
}

function listParams(query: PoiListQuery) {
  return {
    page: query.page,
    limit: query.limit,
    // Newest first, as a list just added to reads.
    sortBy: "createdAt",
    sort: "desc",
    ...searchParam(query.search),
  };
}

function poiFromDto(dto: z.infer<typeof poiDtoSchema>): Poi {
  return { ...dto, remarks: dto.remarks ?? null };
}

/** The body: remarks only when there are some (the field is optional). */
function createBody(input: CreatePoiInput) {
  const remarks = input.remarks?.trim();
  return {
    name: input.name.trim(),
    latitude: input.latitude,
    longitude: input.longitude,
    icon: input.icon,
    ...(remarks === undefined || remarks === "" ? {} : { remarks }),
  };
}

export function createPoisHttpRepository(http: HttpClient): PoisRepository {
  return {
    async list(query) {
      const response = listResponseSchema.parse(
        await http.get(POIS_PATH, { params: listParams(query) }),
      );
      return paginatedFromDto(response, poiFromDto);
    },
    async listForMap(query: PoiMapQuery) {
      const response = mapResponseSchema.parse(
        await http.get(POIS_MAP_PATH, { params: searchParam(query.search) }),
      );
      return response.data.map(poiFromDto);
    },
    async getById(id) {
      const response = detailResponseSchema.parse(
        await http.get(`${POIS_PATH}/${encodeURIComponent(id)}`),
      );
      return poiFromDto(response.data);
    },
    // 400 (invalid location), 403 (no permission, or a staff user), 409 (the
    // company already has the name) and 500 propagate as HttpError carrying
    // the backend's translated message, which the app-wide mutation toast
    // shows ("POI already exists.", "Unable to create POI. Please try again.").
    async create(input) {
      const response = createResponseSchema.parse(
        await http.post(POIS_PATH, createBody(input)),
      );
      return {
        poi: poiFromDto(response.data),
        ...(response.message === undefined
          ? {}
          : { message: response.message }),
      };
    },
    // 409 (another POI has the name), 404, 400 and 500 propagate as
    // HttpError with the backend's translated message, like create's. The
    // body is the input as is: only what changed.
    async update(id, input: UpdatePoiInput) {
      const response = createResponseSchema.parse(
        await http.patch(`${POIS_PATH}/${encodeURIComponent(id)}`, input),
      );
      return {
        poi: poiFromDto(response.data),
        ...(response.message === undefined
          ? {}
          : { message: response.message }),
      };
    },
    // 404 ("POI not found.") and 403 propagate as HttpError, like create's.
    async delete(id) {
      const response = deleteResponseSchema.parse(
        await http.delete(`${POIS_PATH}/${encodeURIComponent(id)}`),
      );
      return response.message === undefined
        ? {}
        : { message: response.message };
    },
    async exportPois(query: PoiExportQuery) {
      const file = await http.get<unknown>(POIS_EXPORT_PATH, {
        // In the list's order: newest first.
        params: {
          sortBy: "createdAt",
          sort: "desc",
          ...searchParam(query.search),
        },
        responseType: "blob",
      });
      if (!(file instanceof Blob)) {
        throw new Error("The POI export didn't come back as a file.");
      }
      return file;
    },
    async downloadTemplate() {
      const file = await http.get<unknown>(POIS_TEMPLATE_PATH, {
        responseType: "blob",
      });
      if (!(file instanceof Blob)) {
        throw new Error("The POI template didn't come back as a file.");
      }
      return file;
    },
    // A file refused as a whole (400 wrong template, missing columns, no or
    // too many records; 413 too large; 415 not .xlsx) propagates as HttpError
    // with the backend's translated message.
    async previewBulkUpload(file) {
      const { data } = bulkPreviewResponseSchema.parse(
        await http.post(POIS_BULK_PREVIEW_PATH, bulkBody(file)),
      );
      return { ...data, rows: data.rows.map(bulkRowFromDto) };
    },
    // As the preview's, plus 400 when no row of the file can be imported.
    async bulkUpload(file) {
      const response = bulkImportResponseSchema.parse(
        await http.post(POIS_BULK_PATH, bulkBody(file)),
      );
      return {
        ...response.data,
        failedRows: response.data.failedRows.map(bulkRowFromDto),
        ...(response.message === undefined
          ? {}
          : { message: response.message }),
      };
    },
    async downloadBulkErrorReport(file) {
      const report = await http.post<unknown>(
        POIS_BULK_ERROR_REPORT_PATH,
        bulkBody(file),
        { responseType: "blob" },
      );
      if (!(report instanceof Blob)) {
        throw new Error("The POI error report didn't come back as a file.");
      }
      return report;
    },
  };
}
